/**
 * Error Display Component
 * Displays errors using Mantine Alert components with enhanced error handling
 */
import React from 'react';
import { DisplayableError } from '../../services/utils/errorHandler';
import { ErrorType } from '../../constants';
import { Alert, ActionIcon, Text, Box, CopyButton } from '@mantine/core';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';

interface ErrorDisplayProps {
  error: DisplayableError | null;
  className?: string;
  variant?: 'filled' | 'outlined' | 'light';
}

export const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
  error,
  className = '',
  variant = 'filled',
}) => {
  const [expanded, setExpanded] = React.useState(false);

  if (!error) return null;

  const getAlertColor = () => {
    switch (error.errorType) {
      case ErrorType.VALIDATION_ERROR:
        return 'yellow' as const;
      case ErrorType.AUTHENTICATION_ERROR:
        return 'red' as const;
      case ErrorType.AUTHORIZATION_ERROR:
        return 'red' as const;
      case ErrorType.NETWORK_ERROR:
        return 'yellow' as const;
      case ErrorType.NOT_FOUND_ERROR:
        return 'blue' as const;
      case ErrorType.INTERNAL_SERVER_ERROR:
        return 'red' as const;
      default:
        return 'red' as const;
    }
  };

  const getAlertTitle = () => {
    switch (error.errorType) {
      case ErrorType.VALIDATION_ERROR:
        return 'Please check your input';
      case ErrorType.AUTHENTICATION_ERROR:
        return 'Authentication Error';
      case ErrorType.AUTHORIZATION_ERROR:
        return 'Access Denied';
      case ErrorType.NETWORK_ERROR:
        return 'Connection Error';
      case ErrorType.NOT_FOUND_ERROR:
        return 'Not Found';
      case ErrorType.INTERNAL_SERVER_ERROR:
        return 'Server Error';
      default:
        return 'Unexpected Error';
    }
  };

  const handleToggleDetails = () => {
    setExpanded(!expanded);
  };

  return (
    <Alert
      color={getAlertColor()}
      variant={variant}
      className={className}
      title={getAlertTitle()}
      withCloseButton={false}
      icon={
        <ActionIcon
          size="sm"
          variant="subtle"
          onClick={handleToggleDetails}
          aria-label={expanded ? 'Hide error details' : 'Show error details'}
        >
          {expanded ? (
            <IconChevronUp size={16} />
          ) : (
            <IconChevronDown size={16} />
          )}
        </ActionIcon>
      }
    >
      {!expanded ? null : (
        // When expanded, show full error details
        <Box>
          {error.errorType === ErrorType.VALIDATION_ERROR && error.details ? (
            <Box>
              {Object.entries(error.details).map(([field, message], index) => (
                <Text key={index} size="sm" mb="xs">
                  • <strong>{field}:</strong> {message}
                </Text>
              ))}
            </Box>
          ) : (
            <>
              <Text size="sm">{error.message}</Text>
              {error.details &&
                Object.keys(error.details).length > 0 &&
                Object.entries(error.details).map(([key, value], index) => (
                  <Text key={index} size="sm" mt="xs" c="dimmed">
                    • <strong>{key.replace(/_/g, ' ')}:</strong> {value}
                  </Text>
                ))}
            </>
          )}

          {error.source === 'server' && error.requestId && (
            <Box mt="xs">
              <Text size="xs" c="dimmed" component="span">
                <strong>Request ID:</strong>{' '}
                <code style={{ fontSize: '0.75rem' }}>{error.requestId}</code>
                {' · '}
              </Text>
              <CopyButton value={error.requestId} timeout={2000}>
                {({ copied, copy }) => (
                  <Text
                    size="xs"
                    component="button"
                    type="button"
                    variant="link"
                    c="dimmed"
                    style={{
                      cursor: 'pointer',
                      background: 'none',
                      border: 'none',
                      padding: 0,
                    }}
                    onClick={copy}
                    aria-label="Copy request ID"
                  >
                    {copied ? 'Copied!' : 'Copy'}
                  </Text>
                )}
              </CopyButton>
            </Box>
          )}

          {error.timestamp && (
            <Text
              size="xs"
              c="dimmed"
              mt="sm"
              pt="sm"
              style={{
                borderTop: '1px solid var(--mantine-color-default-border)',
              }}
            >
              <strong>Timestamp:</strong>{' '}
              {new Date(error.timestamp).toLocaleString()}
            </Text>
          )}
        </Box>
      )}
    </Alert>
  );
};

export default ErrorDisplay;
